import { useState, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Box, Drawer, useMediaQuery } from '@mui/material';
import { AuthContext } from 'auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirestore } from 'utils/firebase';
import theme from 'theme';
import { Sidebar } from '../Sidebar';
import { Topbar } from '../Topbar';
import { Footer } from '../Footer';

const DashboardLayout = ({ viewName, children }) => {
  const isMobile = useMediaQuery('(max-width:1280px)');
  const [isOpen, setIsOpen] = useState(!isMobile);
  const shiftTopbar = isOpen && !isMobile;
  const shiftContent = isOpen && !isMobile;

  const context = useContext(AuthContext);
  const database = useFirestore();

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleToggleOpen = useCallback(() => setIsOpen(prevState => !prevState), []);

  const {
    auth: { user },
    updateUser,
  } = context;

  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    let unsub = () => {};

    if (user?.uid) {
      unsub = onSnapshot(
        doc(database, 'users', user.uid),
        snapshot => {
          const update = { ...snapshot.data(), uid: snapshot.id };
          if (!_.isEqual(update, user)) updateUser(context, update);
        },
        err => console.log(`Encountered error: ${err}`),
      );
    }

    return () => unsub();
  }, [database, user?.uid, user, updateUser, context]);

  return (
    <>
      <Topbar
        sx={{
          position: 'fixed',
          width: '100%',
          top: 0,
          left: 0,
          right: 'auto',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          zIndex: 1000,

          ...(shiftTopbar && {
            marginLeft: '271px',
            width: 'calc(100vw - 271px)',
          }),
        }}
        isSidebarOpen={isOpen}
        onToggleSidebar={handleToggleOpen}
        title={viewName}
      />
      <Drawer
        anchor="left"
        onClose={handleClose}
        open={isOpen}
        variant={isMobile ? 'temporary' : 'persistent'}
        ModalProps={{ keepMounted: true }}
        sx={{
          zIndex: theme.zIndex.drawer,
          '& .MuiDrawer-paper': {
            width: 271,
            border: 'none',
            boxShadow: theme.custom.shadows.sidebar,
            zIndex: theme.zIndex.drawer,
          },
        }}
      >
        <Sidebar />
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: theme.palette.background.default,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),

          display: 'flex',
          minHeight: '100vh',
          flexDirection: 'column',

          ...(shiftContent && {
            marginLeft: '270px',
          }),
        }}
      >
        <Box sx={{ flex: 1, paddingTop: '64px' }}>{children}</Box>
        <Footer className="footer" />
      </Box>
    </>
  );
};

DashboardLayout.propTypes = {
  viewName: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
